<script setup lang="ts">
import { Subscribe } from '~/entities/subscribe';
import {
  CLIENT_API_NEWS_MATERIALS_KEY,
  CLIENT_API_NEWS_MATERIALS_URL,
} from '~/shared/api/client/news/news.const';
import type {
  ClientApiNewsPaginatedResponse,
  INewsListingItemDataClientDTO,
} from '~/shared/api/client/news/news.dto';
import Pagination from '~/shared/ui/pagination/Pagination.vue';
import { SEO_TITLE } from '~/shared/lib/seo';
import { CLIENT_NEWS_ON_PAGE } from '~/pages/news';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { getMaterialLink } from '~/shared/lib/getMaterialLink';
import { updateNewsItemsSeo } from '~/shared/lib/updateNewsItemsSeo';

const searchPhrase = computed(() => {
  return route.query.key ?? '';
});

const route = useRoute();
const router = useRouter();

const pageFromQuery = computed(() => {
  const page = Number(route.query.page);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
});

const pageNumber = useState('client-news-search', () => pageFromQuery.value);

if (pageNumber.value !== pageFromQuery.value) {
  pageNumber.value = pageFromQuery.value;
}

const title = computed(() => {
  const baseTitle = searchPhrase.value
    ? `Новости по запросу «${searchPhrase.value}»`
    : 'Новости';

  return pageNumber.value > 1
    ? `${baseTitle}. Страница ${pageNumber.value}`
    : baseTitle;
});
const seoTitle = computed(() => SEO_TITLE.replace('%%TITLE%%', title.value));

const { data, status: newsStatus } = await useCustomFetch<
  ClientApiNewsPaginatedResponse<INewsListingItemDataClientDTO>
>(CLIENT_API_NEWS_MATERIALS_URL, {
  key: CLIENT_API_NEWS_MATERIALS_KEY,
  method: 'GET',
  watch: [pageNumber],
  params: {
    page: pageNumber,
    page_size: CLIENT_NEWS_ON_PAGE,
    search: searchPhrase,
  },
});

watch(
  () => pageFromQuery.value,
  (value) => {
    if (pageNumber.value !== value) pageNumber.value = value;
  },
);

watch(
  () => pageNumber.value,
  (value) => {
    if (!import.meta.client) return;

    const nextQuery = { ...route.query };

    if (value <= 1) {
      delete nextQuery.page;
    } else {
      nextQuery.page = String(value);
    }

    router.replace({ query: nextQuery });

    nextTick(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  },
);

watch(
  () => data.value,
  (newValue) => {
    if (!import.meta.client) return;

    if (newValue) {
      updateNewsItemsSeo(newValue.results, seoTitle.value);
    }
  },
);

if (data.value?.results?.length && data.value?.results?.length > 0) {
  updateNewsItemsSeo(data.value.results, seoTitle.value);
}
</script>

<template>
  <div :class="$style.static__page">
    <PageHeader
      :title="
        searchPhrase
          ? `Результаты поиска по запросу «${searchPhrase}»`
          : 'Результаты поиска'
      "
      :results="data?.count"
      :class="$style.pageHeader"
      :show-border-bottom="true"
    />
    <div :class="$style.news__wrapper">
      <Loader v-if="newsStatus === 'pending'" />

      <NewsItemCard
        v-for="(item, index) in data?.results"
        v-else-if="data?.results && data.results.length > 0"
        :key="`listing-news-${index}`"
        :title="item.title"
        :category="item.category?.name"
        :category-link="`/category/${item.category?.id}`"
        :date="item?.publication_date"
        :subject="item.subject"
        :link="getMaterialLink(item)"
        :views="item.views"
        :image="item.image?.photo.file_url"
        :new-tab="item.redirect !== null"
      />

      <div
        v-else-if="data?.results && data.results.length === 0"
        :class="$style.noNews"
      >
        <div :class="$style.noNews__title">Новости не найдены</div>
      </div>

      <Pagination
        v-if="newsStatus === 'success'"
        theme="client"
        page-name="client-news-search"
        :total="data?.count || 0"
        :sibling-count="1"
        show-edges
        :items-per-page="CLIENT_NEWS_ON_PAGE"
        :default-page="pageNumber"
        @page-change="pageNumber = $event"
      />
      <div v-else-if="newsStatus === 'error'" :class="$style.noNews">
        <div :class="$style.noNews__title">Не удалось получить новости</div>
      </div>
    </div>

    <Subscribe :class="$style.subscribe" />
  </div>
</template>

<style module lang="scss">
.static__page {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}

.news__wrapper {
  height: 100%;
  flex: 1;
  border-bottom: 1px solid var(--color-gray-line);
}

.noNews {
  padding: var(--space-2-5x);
  &__title {
    @include text_h5;
  }
}

.pageHeader {
  border-bottom: 1px solid var(--color-gray-line);
}

@media (max-width: 479px) {
  .noNews {
    padding: var(--space-2x);
  }
}
</style>
